import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import useAuthStore, { AuthUser } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import AppLoader from './AppLoader';
import VerifyEmailBlocker from './Admin/VerifyEmailBlocker';

interface AuthStore {
   isAuthenticated: boolean;
   user: AuthUser | null;
}

/**
 * Gate for every signed-in route.
 *
 * The verify-email gate lives HERE, not in DashboardLayout. It used to sit in
 * the dashboard shell, which worked while login landed on /admin (inside that
 * shell). The 2026-05-18 surface split moved the landing page to /home
 * (SurfaceRedirect), which is OUTSIDE the shell — so unverified users landed
 * on the one page the verify screen could never render on, watched its two
 * data calls 403 forever, and saw a blank white page with no way forward.
 * Gating at the route level makes it independent of which page you land on.
 */
const PrivateRoute = () => {
   const { isAuthenticated, user } = useAuthStore() as AuthStore;
   const { isLoading, userData } = useAuth();
   const location = useLocation();

   if (isLoading) {
      return <AppLoader />;
   }

   if (!isAuthenticated) {
      // Keep the page (and its query) the visitor was heading to — Login and
      // VerifyEmail send them back there afterwards. smartfi.ph links straight
      // to /partners?apply=distributor (Ray 2026-09-16); without this the
      // sign-in dropped them on /home and the Apply modal never opened.
      return <Navigate to="/" replace state={{ from: `${location.pathname}${location.search}` }} />;
   }

   // Prefer the fresh server answer over the persisted copy (see useAuth).
   // Only an explicit `false` blocks — an absent field means we don't know yet
   // and must not lock anyone out of their own dashboard.
   const verified = userData?.verified ?? user?.verified;
   if (verified === false) {
      return <VerifyEmailBlocker />;
   }

   return <Outlet />;
};

export default PrivateRoute;
